import {
  Flex,
  Heading,
  IconInfoSolid,
  RadioInput,
  RadioInputGroup,
  Text,
  ToggleDetails,
  Tooltip,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import LineChart from "../../sections/Cards/LineChart";
import { CourseLevelService } from "../../services/CourseLevelService";
import { OutcomeCategoryService } from "../../services/OutcomeCategoryService";
import { TermsContext } from "../../utils/Context";
import { MULTI, Selector } from "../Selectors/Selector";
import VarietyCheckbox from "../Selectors/Variety/VarietyCheckbox";
import AcademicYearSelect from "../TermManagement/AcademicYears/AcademicYearSelect";
import { TermSelect } from "../TermManagement/Terms/TermSelect";
import OutcomeData from "./Review/OutcomeData";

const Chart = ({
  filters,
  dispatch,
  trigger,
  setTrigger,
  clear,
  subjects,
  selectedCourses,
}) => {
  const { t } = useTranslation();
  const { insightsTerms } = useContext(TermsContext);
  const [reloadOutcomeResults, setReloadOutcomeResults] = useState(true);
  const [categories, setCategories] = useState([]);
  const [clearCatAndProgram, setClearCatAndProgram] = useState(false);
  const [courseLevels, setCourseLevels] = useState([]);
  const [selectedAcademicYears, setSelectedAcademicYears] = useState([]);
  const [selectedInsightsTerms, setSelectedInsightsTerms] = useState([]);

  const loInputs = [
    { value: "ISLO", label: "ILO" },
    { value: "PSLO", label: "PLO" },
    { value: "CSLO", label: "CLO" },
  ];

  const measuredAt = [
    { value: "true", label: `${t("dashboard.achievedMastery")} %` },
    { value: "false", label: `${t("dashboard.scoreAvg")} %` },
  ];

  useEffect(() => {
    if (clear) {
      setSelectedAcademicYears([]);
      setSelectedInsightsTerms([]);
    }
  }, [clear]);

  useEffect(() => {
    CourseLevelService.getCourseLevels().then((levels) => {
      setCourseLevels(levels);
    });
  }, []);

  useEffect(() => {
    if (filters.LOType === "ISLO") {
      OutcomeCategoryService.getCategoryByType(filters.LOType).then((cat) => {
        setCategories(
          cat.map((c) => {
            return { id: c.id, label: c.name };
          })
        );
      });
    }
  }, [filters.LOType]);

  useEffect(() => {
    if (filters.LOType === "PSLO") {
      const programList = [];
      filters.programs.forEach((p) => {
        programList.push(
          OutcomeCategoryService.getOutcomeCategoriesByContext(p)
        );
      });
      Promise.all(programList).then((cats) => {
        cats = cats.flat();
        setCategories(
          cats.map((c) => {
            return { id: c.id, label: c.name };
          })
        );
      });
    }
  }, [filters.LOType, filters.programs]);

  const handleLOTypeChange = (event) => {
    setClearCatAndProgram(true);
    dispatch({
      type: "UPDATE_FILTER",
      field: "LOType",
      value: event.target.value,
    });
    dispatch({ type: "UPDATE_FILTER", field: "categories", value: [] });
    if (event.target.value === "CSLO") {
      dispatch({ type: "UPDATE_FILTER", field: "variety", value: ["outcome"] });
      dispatch({ type: "UPDATE_FILTER", field: "type", value: [] });
    }
  };

  const handleTypeChange = (values) => {
    dispatch({ type: "UPDATE_FILTER", field: "variety", value: values });
  };

  const handleCategorySelect = (categories) => {
    setClearCatAndProgram(false);
    dispatch({ type: "UPDATE_FILTER", field: "categories", value: categories });
  };

  const handleResultsTypeChange = (event) => {
    dispatch({
      type: "UPDATE_FILTER",
      field: "masteryResults",
      value: event.target.value,
    });
  };

  const handleAcademicYearSelect = (years) => {
    setSelectedAcademicYears(years);
    // Filter out future insights terms
    let filteredInsightsTerms = years.flatMap((year) => year.insights_terms);
    filteredInsightsTerms = filteredInsightsTerms.filter(
      (term) =>
        new Date(term.start_at).setHours(0, 0, 0, 0) <=
        new Date().setHours(0, 0, 0, 0)
    );
    handleSelectedTerms(filteredInsightsTerms.map((t) => t.id));
  };

  const handleSelectedTerms = (terms) => {
    setSelectedInsightsTerms(terms);
    // Get all canvas terms for selected insights terms
    const canvasTermIdList = [];
    const selectedTerms = insightsTerms.filter((t) => terms.includes(t.id));
    selectedTerms.forEach((term) => {
      canvasTermIdList.push(
        ...term.canvas_terms.map((canvasTerm) => canvasTerm.canvas_id)
      );
    });
    dispatch({
      type: "UPDATE_FILTER",
      field: "terms",
      value: canvasTermIdList,
    });
  };

  const getGraphTitle = () => {
    const levelObjs = courseLevels.filter((level) =>
      filters.courseLevels.includes(level.id)
    );
    const levelsString = levelObjs
      .map((level) => `${t("courseCodePattern.level")} ${level.courseLevel}`)
      .join(", ");

    const LOType = filters.LOType;
    if (filters.courses.length > 0) {
      const resultsFor =
        filters.masteryResults === "true"
          ? t("dashboard.masteryOverTime")
          : t("dashboard.gradeOverTime");
      return `${LOType} ${resultsFor}`;
    } else {
      const resultsFor =
        filters.masteryResults === "true"
          ? t("dashboard.masteryOverTimeFor")
          : t("dashboard.gradeOverTimeFor");
      return `${LOType} ${resultsFor} ${filters.subject} ${
        levelsString ? `(${levelsString})` : ""
      }`;
    }
  };

  return (
    <div style={{ textAlign: "center" }}>
      <ToggleDetails
        fluidWidth
        iconPosition="start"
        size="medium"
        onToggle={(_e, expanded) => expanded && setReloadOutcomeResults(true)}
        summary={
          <Flex justifyItems="space-between" wrap="wrap">
            <Flex.Item>
              <Heading as="h2" level="h4">
                {t("dashboard.loResults")}
              </Heading>
            </Flex.Item>
            <Flex.Item>
              <OutcomeData
                reload={reloadOutcomeResults}
                setReload={setReloadOutcomeResults}
                subject={filters.subject}
                searchTrigger={trigger}
              />
            </Flex.Item>
          </Flex>
        }
      >
        <div style={{ textAlign: "left", marginTop: "5px" }}>
          <Text>{t("dashboard.disclaimer")} </Text>
          <Tooltip
            renderTip={
              <div style={{ width: "600px" }}>
                {t("dashboard.chartDataDisclaimer")}
              </div>
            }
            placement="end"
            on={["click", "hover", "focus"]}
            color="primary-inverse"
          >
            <IconInfoSolid color="brand" />
          </Tooltip>
        </div>
        <Flex wrap="wrap" alignItems="center" margin="medium 0 0 0">
          <LineChart
            filters={filters}
            trigger={trigger}
            clear={clear}
            inAnalyze={true}
            setTrigger={setTrigger}
            subs={subjects}
            customTitle={getGraphTitle()}
          />
          <Flex.Item shouldGrow padding="small">
            <Flex alignItems="start" gap="large" justifyItems="center">
              <Flex.Item shouldShrink>
                <Flex direction="column" gap="small">
                  <Flex.Item overflowY="visible">
                    <RadioInputGroup
                      layout="columns"
                      name="loType"
                      value={filters.LOType}
                      description={
                        <Text size="small">{t("filter.loType")}</Text>
                      }
                      size="small"
                      onChange={handleLOTypeChange}
                    >
                      {loInputs.map((input) => (
                        <RadioInput
                          key={input.value}
                          value={input.value}
                          label={input.label}
                          disabled={
                            input.value === "CSLO" &&
                            selectedCourses.length === 0
                          }
                        />
                      ))}
                    </RadioInputGroup>
                  </Flex.Item>
                  <Flex.Item overflowY="visible">
                    <VarietyCheckbox
                      filters={filters}
                      selectHandler={handleTypeChange}
                    />
                  </Flex.Item>
                </Flex>
              </Flex.Item>
              <Flex.Item size="30%">
                <Selector
                  label={t("categories.selectCategory")}
                  id="category-select"
                  type={MULTI}
                  size="small"
                  options={categories}
                  selectHandler={handleCategorySelect}
                  placeholder={t("categories.selectCategoryPlaceholder")}
                  clear={clearCatAndProgram || clear}
                  interaction={
                    (filters.LOType === "PSLO" &&
                      filters.programs.length > 0) ||
                    filters.LOType === "ISLO"
                  }
                />
              </Flex.Item>
              <Flex.Item shouldShrink>
                <RadioInputGroup
                  layout="stacked"
                  name="measured"
                  value={filters.masteryResults}
                  description={
                    <Text size="small">{t("filter.masteryResults")}</Text>
                  }
                  size="small"
                  onChange={handleResultsTypeChange}
                >
                  {measuredAt.map((input) => (
                    <RadioInput
                      key={input.value}
                      value={input.value}
                      label={input.label}
                    />
                  ))}
                </RadioInputGroup>
              </Flex.Item>
              <Flex.Item size="30%">
                <Flex direction="column">
                  <Flex.Item overflowY="visible">
                    <AcademicYearSelect
                      selectHandler={handleAcademicYearSelect}
                      selectedAcademicYears={selectedAcademicYears.map(
                        (year) => year.id
                      )}
                      size="small"
                      noFutureYears
                      clear={clear}
                    />
                  </Flex.Item>
                  <Flex.Item overflowY="visible" margin="small 0 0 0">
                    <TermSelect
                      label={t("terms")}
                      selectHandler={handleSelectedTerms}
                      selectedAcademicYears={selectedAcademicYears}
                      size="small"
                      selectedTerms={selectedInsightsTerms}
                      noFutureTerms
                      clear={clear}
                    />
                  </Flex.Item>
                </Flex>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </ToggleDetails>
    </div>
  );
};

export default Chart;
